import React, { useState } from 'react';
import './RevolutionaryLayoutSettings.css';

/**
 * 🚀 Revolutionary Layout Settings 2025
 * 
 * Standardisierte Layout-Komponente für alle Widgets mit:
 * - Einheitliche Layout-Properties (padding, margin, width, height, etc.)
 * - Mobile/Desktop Responsive Toggles
 * - Smart Sliders mit gesunden Einschränkungen
 * - Design passend zum Text Refactoring 2025
 */
function RevolutionaryLayoutSettings({ editData, handleInputChange, customProperties = [], excludeSections = [] }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeDevice, setActiveDevice] = useState('desktop'); // desktop | mobile
    const [enabledProperties, setEnabledProperties] = useState({}); // Toggle states für toggleable properties

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Standard Layout Properties - vereinfacht und optimiert
    const standardLayoutProperties = [
        // SPACING SECTION - nur vertikal
        {
            section: 'Spacing',
            icon: 'fa-arrows-v', // FontAwesome Icon für Spacing
            properties: [
                {
                    id: 'paddingTop',
                    label: 'Padding Top',
                    min: 0,
                    max: 200,
                    step: 5,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Inner spacing from top edge'
                },
                {
                    id: 'paddingBottom',
                    label: 'Padding Bottom',
                    min: 0,
                    max: 200,
                    step: 5,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Inner spacing from bottom edge'
                },
                {
                    id: 'marginTop',
                    label: 'Margin Top',
                    min: 0,
                    max: 300,
                    step: 5,
                    unit: 'px',
                    icon: '⬆️',
                    description: 'Outer spacing above widget'
                },
                {
                    id: 'marginBottom',
                    label: 'Margin Bottom',
                    min: 0,
                    max: 300,
                    step: 5,
                    unit: 'px',
                    icon: '⬇️',
                    description: 'Outer spacing below widget'
                }
            ]
        },
        // HEIGHT DIMENSIONS SECTION - nur Vertikale
        {
            section: 'Height Settings',
            icon: 'fa-expand', // FontAwesome Icon für Height (expand icon)
            properties: [
                {
                    id: 'height',
                    label: 'Height',
                    min: 100,
                    max: 2000,
                    step: 10,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Widget height',
                    allowAuto: true,
                    defaultValue: 650  // ✅ FIX: Standard Height Default
                },
                {
                    id: 'minHeight',
                    label: 'Min Height',
                    min: 50,
                    max: 2000,
                    step: 10,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Minimum widget height'
                },
                {
                    id: 'maxHeight',
                    label: 'Max Height',
                    min: 200,
                    max: 2000,
                    step: 10,
                    unit: 'px',
                    icon: '↕️',
                    description: 'Maximum widget height',
                    allowNone: true,
                    toggleable: true
                }
            ]
        }
    ];

    // Mobile Controls Section - nur für HeroHeader mit Side-by-Side Layouts
    const mobileControlsProperties = [
        {
            section: 'Mobile Controls',
            icon: 'fa-mobile',
            properties: [
                {
                    id: 'desktopFlipSides',
                    label: 'Flip Sides on Desktop',
                    type: 'toggle',
                    icon: '🔄',
                    description: 'Switch left and right sides on desktop devices',
                    defaultValue: false,
                    desktopOnly: true // Nur im Desktop-Modus anzeigen
                },
                {
                    id: 'mobileFlipSides',
                    label: 'Flip Sides on Mobile',
                    type: 'toggle',
                    icon: '🔄',
                    description: 'Switch left and right sides on mobile devices',
                    defaultValue: false,
                    mobileOnly: true // Nur im Mobile-Modus anzeigen
                },
                {
                    id: 'heroImageMaxHeight',
                    label: 'Hero Image Max Height',
                    min: 200,
                    max: 800,
                    step: 20,
                    unit: 'px',
                    icon: '📐',
                    description: 'Maximum height of hero image on mobile devices',
                    defaultValue: 300,
                    mobileOnly: true,
                    forceMobileField: true, // Force use heroImageMaxHeightMobile field
                    toggleable: true, // Can be enabled/disabled
                    allowAuto: false,
                    allowNone: false
                },
                {
                    id: 'logoWidth',
                    label: 'Logo Width',
                    min: 80,
                    max: 250,
                    step: 10,
                    unit: 'px',
                    icon: '🏷️',
                    description: 'Width of logo on mobile devices',
                    defaultValue: 120,
                    mobileOnly: true,
                    forceMobileField: true // Force use logoWidthMobile field
                }
            ]
        }
    ];

    // Filter Standard Properties basierend auf excludeSections
    const filteredStandardProperties = standardLayoutProperties.filter(section => 
        !excludeSections.includes(section.section)
    );
    
    // Kombiniere gefilterte Standard + Custom Properties + Mobile Controls
    const allProperties = [
        ...(filteredStandardProperties || []), 
        ...(mobileControlsProperties || []), 
        ...(customProperties || [])
    ];

    // Get responsive field name
    const getFieldName = (propertyId, property = null) => {
        // For properties with forceMobileField, always use the Mobile version
        if (property && property.forceMobileField) {
            return `${propertyId}Mobile`;
        }
        
        // ✅ KRITISCH: Auch Custom Properties mit responsive: true unterstützen
        if (property && property.responsive && activeDevice === 'mobile') {
            return `${propertyId}Mobile`;
        }
        
        if (activeDevice === 'mobile') {
            return `${propertyId}Mobile`;
        }
        return propertyId;
    };

    // Get current value with fallbacks
    const getCurrentValue = (propertyId, defaultValue = 0, property = null) => {
        const fieldName = getFieldName(propertyId, property);
        const value = editData[fieldName];
        
        // ✅ FIX: Use property-specific default value if available
        const effectiveDefault = defaultValue;
        
        // ✅ DEBUG: Log current values for height properties (reduced logging)
        if (propertyId === 'height' && (value === undefined || value === null)) {
            console.log(`🔍 RevolutionaryLayoutSettings: ${fieldName} is undefined, using default:`, effectiveDefault);
        }
        
        if (value === undefined || value === null || value === '') {
            return effectiveDefault;
        }
        
        // Handle string values like "auto", "none"
        if (typeof value === 'string' && isNaN(value)) {
            return value;
        }
        
        // ✅ FIX: Konvertiere String-Numbers zu Numbers
        const numericValue = typeof value === 'string' ? parseInt(value) : value;
        return !isNaN(numericValue) ? numericValue : effectiveDefault;
    };

    // Handle slider change
    const handleSliderChange = (propertyId, value, property) => {
        const fieldName = getFieldName(propertyId, property);
        
        // Create fake event for consistency with existing system
        const fakeEvent = {
            target: {
                name: fieldName,
                value: value,
                type: 'range'
            }
        };
        
        console.log(`🔧 Slider change: ${propertyId} → ${fieldName} = ${value}`, {
            property: property,
            responsive: property?.responsive,
            activeDevice: activeDevice,
            editDataBefore: editData[fieldName],
            eventTarget: fakeEvent.target
        });
        
        handleInputChange(fakeEvent);
    };

    // Handle special value change (auto, none, etc.)
    const handleSpecialValueChange = (propertyId, value) => {
        const fieldName = getFieldName(propertyId);
        
        const fakeEvent = {
            target: {
                name: fieldName,
                value: value,
                type: 'select'
            }
        };
        
        handleInputChange(fakeEvent);
    };

    // Handle property toggle for toggleable properties
    const handlePropertyToggle = (propertyId) => {
        const newEnabledState = !enabledProperties[propertyId];
        
        setEnabledProperties(prev => ({
            ...prev,
            [propertyId]: newEnabledState
        }));
        
        // Wenn Property deaktiviert wird, setze den Wert auf 'none' oder leer
        if (!newEnabledState) {
            const fieldName = getFieldName(propertyId);
            const fakeEvent = {
                target: {
                    name: fieldName,
                    value: '', // Leerer Wert = Property deaktiviert
                    type: 'toggle'
                }
            };
            handleInputChange(fakeEvent);
        }
    };

    // Check if property is enabled (for toggleable properties)
    const isPropertyEnabled = (property) => {
        if (!property.toggleable) return true;
        
        // Prüfe ob Property einen Wert hat
        const currentValue = getCurrentValue(property.id);
        if (currentValue !== 0 && currentValue !== '' && currentValue !== null && currentValue !== undefined) {
            return true;
        }
        
        return enabledProperties[property.id] !== false;
    };

    // Render property slider
    const renderPropertySlider = (property) => {
        // Skip mobile-only properties when in desktop mode
        if (property.mobileOnly && activeDevice === 'desktop') {
            return null;
        }
        
        // Skip desktop-only properties when in mobile mode
        if (property.desktopOnly && activeDevice === 'mobile') {
            return null;
        }
        
        const currentValue = getCurrentValue(property.id, property.defaultValue || 0, property);
        const isSpecialValue = typeof currentValue === 'string';
        const propertyEnabled = isPropertyEnabled(property);
        
        // Handle Toggle Properties
        if (property.type === 'toggle') {
            const toggleValue = editData[property.id] !== undefined ? editData[property.id] : property.defaultValue;
            
            return (
                <div key={property.id} className="revolutionary-layout-property toggle-property">
                    <div className="property-header">
                        <div className="property-label-row">
                            <span className="property-icon">{property.icon}</span>
                            <label className="property-label">{property.label}</label>
                            
                            <div className="property-toggle-container">
                                <button
                                    type="button"
                                    className={`property-toggle-btn ${toggleValue ? 'enabled' : 'disabled'}`}
                                    onClick={() => {
                                        const event = {
                                            target: {
                                                name: property.id,
                                                checked: !toggleValue,
                                                type: 'checkbox'
                                            }
                                        };
                                        
                                        console.log(`🔘 Toggle change: ${property.id} = ${!toggleValue}`, {
                                            property: property,
                                            editDataBefore: editData[property.id],
                                            eventTarget: event.target
                                        });
                                        
                                        handleInputChange(event);
                                    }}
                                    title={`${toggleValue ? 'Disable' : 'Enable'} ${property.label}`}
                                >
                                    <i className={`fa ${toggleValue ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                                </button>
                            </div>
                            
                            <div className="property-value-display">
                                {toggleValue ? 'ON' : 'OFF'}
                            </div>
                        </div>
                    </div>
                    
                    {property.description && (
                        <div className="property-description">
                            {property.description}
                        </div>
                    )}
                </div>
            );
        }
        
        return (
            <div key={property.id} className="revolutionary-layout-property">
                <div className="property-header">
                    <div className="property-label-row">
                        <span className="property-icon">{property.icon}</span>
                        <label className="property-label">{property.label}</label>
                        
                        {/* Toggle Switch für toggleable properties */}
                        {property.toggleable && (
                            <div className="property-toggle-container">
                                <button
                                    type="button"
                                    className={`property-toggle-btn ${propertyEnabled ? 'enabled' : 'disabled'}`}
                                    onClick={() => handlePropertyToggle(property.id)}
                                    title={`${propertyEnabled ? 'Disable' : 'Enable'} ${property.label}`}
                                >
                                    <i className={`fa ${propertyEnabled ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                                </button>
                            </div>
                        )}
                        
                        <div className="property-value-display">
                            {!propertyEnabled ? 'Disabled' : (isSpecialValue ? currentValue : `${currentValue}${property.unit}`)}
                        </div>
                    </div>
                    
                    {/* Special Values (Auto, None) - nur wenn Property enabled */}
                    {propertyEnabled && (property.allowAuto || property.allowNone) && (
                        <div className="special-values">
                            {property.allowAuto && (
                                <button
                                    type="button"
                                    className={`special-value-btn ${currentValue === 'auto' ? 'active' : ''}`}
                                    onClick={() => handleSpecialValueChange(property.id, 'auto')}
                                    title="Auto sizing"
                                >
                                    Auto
                                </button>
                            )}
                            {property.allowNone && (
                                <button
                                    type="button"
                                    className={`special-value-btn ${currentValue === 'none' ? 'active' : ''}`}
                                    onClick={() => handleSpecialValueChange(property.id, 'none')}
                                    title="No constraint"
                                >
                                    None
                                </button>
                            )}
                            {isSpecialValue && (
                                <button
                                    type="button"
                                    className="special-value-btn"
                                    onClick={() => handleSpecialValueChange(property.id, property.min || 0)}
                                    title="Use custom value"
                                >
                                    Custom
                                </button>
                            )}
                        </div>
                    )}
                </div>
                
                {/* Slider (only show if property enabled and not special value) */}
                {propertyEnabled && !isSpecialValue && (
                    <div className="property-slider-container">
                        <input
                            type="range"
                            min={property.min}
                            max={property.max}
                            step={property.step}
                            value={currentValue}
                            onChange={(e) => handleSliderChange(property.id, parseInt(e.target.value), property)}
                            className="revolutionary-slider"
                        />
                        <div className="slider-track-labels">
                            <span className="track-label-min">{property.min}{property.unit}</span>
                            <span className="track-label-max">{property.max}{property.unit}</span>
                        </div>
                    </div>
                )}
                
                {property.description && (
                    <div className="property-description">
                        {property.description}
                    </div>
                )}
            </div>
        );
    };

    // Count total properties
    const totalProperties = allProperties.reduce((sum, section) => sum + section.properties.length, 0);

    return (
        <div className="revolutionary-layout-container">
            {/* Revolutionary Header */}
            <div 
                className={`revolutionary-layout-header ${isExpanded ? 'expanded' : ''}`}
                onClick={toggleExpanded}
            >
                <div className="layout-header-content">
                    <div className="layout-icon">
                        <i className="fa fa-th-large"></i>
                    </div>
                    <div className="layout-title">Layout</div>
                    <div className="layout-count">{totalProperties}</div>
                </div>
                <div className={`layout-toggle-arrow ${isExpanded ? 'rotated' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="revolutionary-layout-content">
                    {/* Device Selector - wie Button Style */}
                    <div className="device-selector-section" style={{ padding: '0px' }}>
                        <div className="device-selector-header">
                            <span className="device-selector-label">📱 Device Preview</span>
                        </div>
                        <div className="device-selector-buttons">
                            <button 
                                type="button"
                                className={`device-btn ${activeDevice === 'desktop' ? 'active' : ''}`}
                                onClick={() => setActiveDevice('desktop')}
                            >
                                <i className="fa fa-desktop"></i>
                                <span>Desktop</span>
                            </button>
                            <button 
                                type="button"
                                className={`device-btn ${activeDevice === 'mobile' ? 'active' : ''}`}
                                onClick={() => setActiveDevice('mobile')}
                            >
                                <i className="fa fa-mobile"></i>
                                <span>Mobile</span>
                            </button>
                        </div>
                    </div>

                    {/* Property Sections */}
                    {allProperties.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="property-section">
                            <div className="section-header">
                                <i className={`fa ${section.icon}`}></i>
                                <span className="section-title">{section.section}</span>
                            </div>
                            
                            <div className="properties-grid">
                                {section.properties.map(renderPropertySlider)}
                            </div>
                        </div>
                    ))}

                    {/* Info Footer */}
                    <div className="layout-info-footer">
                        <div className="info-icon">💡</div>
                        <div className="info-text">
                            All settings are responsive. Switch between Desktop and Mobile to customize for different screen sizes.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RevolutionaryLayoutSettings;
